/**
 * RunDashboard — inline web view of a run's ReportContent (P10h — AC-P10.8).
 *
 * Routed at /runs/:id/dashboard. Renders the same per-fragment-kind
 * surfaces the DOCX/PDF/PPTX export tools produce (narrative,
 * metric, table, chart, callout, section), but as React. No backend
 * pipeline-run-summary endpoint exists yet — for the pilot path the
 * route accepts ?demo=true to render a synthetic FSA-NSLDS sample
 * so the page is reachable end-to-end. Wiring a real run-summary
 * fetch is a documented follow-on.
 *
 * Also surfaces three download buttons (DOCX / PDF / PPTX) that call
 * the P10g `POST /api/v2/reports` endpoint and trigger a browser
 * download.
 */

import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";

import {
  reportsV2,
  type ReportContent,
  type ReportFragment,
  type ReportFragmentKind,
} from "../../../apiv2";

interface Props {
  onBack?: () => void;
}

// Deloitte palette anchors — match report-templates.deloitte@1.0.0.
const PALETTE = {
  primary: "#006A4E",
  secondary: "#0F3460",
  accent: "#86BC25",
  text: "#1A1A2E",
  muted: "#666666",
  surface: "#F8F9FA",
  rule: "#E0E0E0",
  callout_info: "#DBEAFE",
  callout_warning: "#FEF3C7",
  callout_danger: "#FEE2E2",
};

const SECTION_ORDER: Record<string, number> = {
  executive_summary: 0,
  classification_findings: 1,
  findings: 2,
  recommendations: 3,
  appendix: 4,
};

function sectionTitle(key: string): string {
  return key
    .split("_")
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(" ");
}

function FragmentBody({
  kind,
  payload,
}: {
  kind: ReportFragmentKind;
  payload: Record<string, unknown>;
}) {
  if (kind === "narrative") {
    const raw = payload?.paragraphs;
    const paragraphs = Array.isArray(raw)
      ? raw.map(String)
      : typeof raw === "string"
        ? [raw]
        : [];
    return (
      <div
        className="space-y-3 text-[15px] leading-relaxed"
        style={{ color: PALETTE.text }}
      >
        {paragraphs.map((p, i) => (
          <p key={i}>{p}</p>
        ))}
      </div>
    );
  }
  if (kind === "metric") {
    return (
      <div className="text-center py-6">
        <div className="text-5xl font-bold" style={{ color: PALETTE.primary }}>
          {String(payload?.value ?? "—")}
        </div>
        {payload?.label != null && (
          <div className="text-sm mt-2" style={{ color: PALETTE.muted }}>
            {String(payload.label)}
          </div>
        )}
      </div>
    );
  }
  if (kind === "table") {
    const columns = Array.isArray(payload?.columns)
      ? (payload.columns as string[])
      : [];
    const rows = Array.isArray(payload?.rows)
      ? (payload.rows as unknown[][])
      : [];
    if (columns.length === 0) {
      return (
        <p style={{ color: PALETTE.muted }} className="italic">
          [empty table]
        </p>
      );
    }
    return (
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr>
            {columns.map((c, i) => (
              <th
                key={i}
                style={{ background: PALETTE.secondary, color: "#FFF" }}
                className="text-left px-3 py-2 font-semibold"
              >
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            <tr
              key={ri}
              style={ri % 2 === 1 ? { background: PALETTE.surface } : undefined}
            >
              {columns.map((_, ci) => (
                <td
                  key={ci}
                  style={{ borderBottom: `1px solid ${PALETTE.rule}` }}
                  className="px-3 py-2"
                >
                  {ci < row.length ? String(row[ci]) : ""}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    );
  }
  if (kind === "chart") {
    const chartType = String(payload?.chart_type ?? "chart");
    const altText = String(payload?.alt_text ?? `${chartType} visualisation`);
    return (
      <div
        className="rounded-lg p-8 text-center"
        style={{
          background: PALETTE.surface,
          border: `1px solid ${PALETTE.rule}`,
        }}
        role="img"
        aria-label={altText}
      >
        <div className="text-lg italic mb-2" style={{ color: PALETTE.muted }}>
          [Chart: {chartType}]
        </div>
        <div className="text-xs" style={{ color: PALETTE.muted }}>
          Alt-text: {altText}
        </div>
      </div>
    );
  }
  if (kind === "callout") {
    const severity = String(payload?.severity ?? "info").toLowerCase();
    const message = String(payload?.message ?? "");
    const bg =
      severity === "danger"
        ? PALETTE.callout_danger
        : severity === "warning"
          ? PALETTE.callout_warning
          : PALETTE.callout_info;
    return (
      <div
        className="rounded p-3"
        style={{ background: bg, borderLeft: `4px solid ${PALETTE.primary}` }}
      >
        <span className="font-bold mr-2">{severity.toUpperCase()}:</span>
        <span>{message}</span>
      </div>
    );
  }
  // "section" kind = title-only.
  return null;
}

function FragmentCard({ frag }: { frag: ReportFragment }) {
  return (
    <article
      className="rounded-lg p-4 mb-4"
      style={{ background: "#FFFFFF", border: `1px solid ${PALETTE.rule}` }}
      data-testid={`fragment-${frag.skill_name}`}
    >
      <h3
        className="text-lg font-semibold mb-3"
        style={{ color: PALETTE.secondary }}
      >
        {frag.title}
      </h3>
      <FragmentBody
        kind={frag.kind}
        payload={(frag.payload ?? {}) as Record<string, unknown>}
      />
    </article>
  );
}

const DEMO_CONTENT: ReportContent = {
  run_id: "fsa-nslds-demo-run",
  project_id: "fsa-nslds-demo",
  title: "FSA-NSLDS Quality Run",
  branding_mode: "client_custom",
  sections: {
    executive_summary: [
      {
        skill_name: "reporting.generate_narrative",
        kind: "narrative",
        title: "Run summary",
        order: 0,
        section: "executive_summary",
        payload: {
          paragraphs: [
            "This run profiled 17 tables, scanned 15,000,000 rows, completed in 4 minutes 12 seconds.",
            "Overall data quality scored 87/100. 11 data quality issues surfaced, 3 PII findings flagged.",
            "The largest contributing tables were: loans, borrowers, schools.",
          ],
        },
      },
      {
        skill_name: "profiler.score_quality",
        kind: "metric",
        title: "Overall DQ score",
        section: "executive_summary",
        payload: { value: "87/100", label: "Across 17 tables" },
      },
    ],
    findings: [
      {
        skill_name: "profiler.profile_structural",
        kind: "table",
        title: "Per-table row counts",
        section: "findings",
        payload: {
          columns: ["Table", "Rows", "Null %"],
          rows: [
            ["loans", "12,300,000", "0.3"],
            ["borrowers", "8,750,000", "0.1"],
            ["schools", "6,200", "0.0"],
          ],
        },
      },
      {
        skill_name: "profiler.detect_pii",
        kind: "callout",
        title: "PII findings",
        section: "findings",
        payload: {
          severity: "warning",
          message: "3 columns flagged as RESTRICTED in `borrowers`.",
        },
      },
    ],
  },
  metadata: { generated_at: "2026-05-27T12:00:00Z" },
};

export function RunDashboard({ onBack }: Props) {
  const { id: runId } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const isDemo = searchParams.get("demo") === "true";

  // For now the only source of a real ReportContent is the demo
  // payload; once the pipeline-run-summary endpoint lands this
  // component fetches it by runId.
  const content: ReportContent | null = useMemo(() => {
    if (isDemo)
      return { ...DEMO_CONTENT, run_id: runId ?? DEMO_CONTENT.run_id };
    return null;
  }, [isDemo, runId]);

  const [downloading, setDownloading] = useState<
    null | "docx" | "pdf" | "pptx"
  >(null);
  const [phase, setPhase] = useState<null | "queued" | "generating">(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setError(null);
  }, [runId]);

  const orderedSectionKeys = useMemo(() => {
    if (!content) return [];
    return Object.keys(content.sections).sort((a, b) => {
      const ao = SECTION_ORDER[a] ?? 99;
      const bo = SECTION_ORDER[b] ?? 99;
      return ao - bo;
    });
  }, [content]);

  async function handleDownload(format: "docx" | "pdf" | "pptx") {
    if (!content) return;
    setDownloading(format);
    setPhase("generating");
    setError(null);
    try {
      // Render + download in one request — no Celery job, no polling (the
      // async job path hung indefinitely in production; all three formats
      // render fast enough in-process, same as the Excel-style pattern
      // used elsewhere in the app).
      await reportsV2.renderAndDownload(
        { content, format, use_branding_mode: true },
        `ztd-report-${content.run_id}.${format}`,
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : "Render failed");
    } finally {
      setDownloading(null);
      setPhase(null);
    }
  }

  if (!content) {
    return (
      <div className="p-8 max-w-4xl mx-auto">
        {onBack && (
          <button
            onClick={onBack}
            className="mb-4 text-sm underline"
            style={{ color: PALETTE.secondary }}
          >
            ← Back
          </button>
        )}
        <div
          className="rounded-lg p-6 text-center"
          style={{
            background: PALETTE.surface,
            border: `1px solid ${PALETTE.rule}`,
          }}
        >
          <h2
            className="text-xl font-semibold mb-2"
            style={{ color: PALETTE.secondary }}
          >
            Run dashboard
          </h2>
          <p style={{ color: PALETTE.muted }}>
            Real-run report summary is not wired up yet. Append{" "}
            <code className="px-1 rounded bg-white/40">?demo=true</code> to the
            URL to preview the dashboard with synthetic FSA-NSLDS data.
          </p>
          <p className="text-xs mt-4" style={{ color: PALETTE.muted }}>
            (Run ID: {runId ?? "—"})
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-5xl mx-auto" data-testid="run-dashboard">
      {onBack && (
        <button
          onClick={onBack}
          className="mb-4 text-sm underline"
          style={{ color: PALETTE.secondary }}
        >
          ← Back
        </button>
      )}

      {/* Header strip */}
      <header
        className="rounded-lg p-6 mb-6"
        style={{
          background: PALETTE.surface,
          borderTop: `4px solid ${PALETTE.primary}`,
          border: `1px solid ${PALETTE.rule}`,
        }}
      >
        <h1 className="text-3xl font-bold" style={{ color: PALETTE.secondary }}>
          {content.title}
        </h1>
        <div className="text-sm mt-2" style={{ color: PALETTE.muted }}>
          Project {content.project_id} • Run {content.run_id} • Branding:{" "}
          {content.branding_mode ?? "deloitte"}
        </div>
      </header>

      {/* Download bar */}
      <div
        className="flex gap-2 mb-6"
        role="toolbar"
        aria-label="Download report"
      >
        {(["docx", "pdf", "pptx"] as const).map((fmt) => (
          <button
            key={fmt}
            onClick={() => handleDownload(fmt)}
            disabled={downloading !== null}
            className="px-4 py-2 rounded text-sm font-medium disabled:opacity-50"
            style={{
              background: PALETTE.primary,
              color: "#FFFFFF",
            }}
            data-testid={`download-${fmt}`}
          >
            {downloading === fmt
              ? phase === "generating"
                ? "Generating…"
                : "Queued…"
              : `Download ${fmt.toUpperCase()}`}
          </button>
        ))}
      </div>

      {error && (
        <div
          className="rounded p-3 mb-4 text-sm"
          style={{ background: PALETTE.callout_danger, color: PALETTE.text }}
          role="alert"
        >
          {error}
        </div>
      )}

      {/* Sections */}
      {orderedSectionKeys.map((key) => (
        <section key={key} className="mb-8" data-testid={`section-${key}`}>
          <h2
            className="text-2xl font-bold mb-4 pb-2"
            style={{
              color: PALETTE.secondary,
              borderBottom: `2px solid ${PALETTE.primary}`,
            }}
          >
            {sectionTitle(key)}
          </h2>
          {content.sections[key].map((frag, i) => (
            <FragmentCard key={`${frag.skill_name}-${i}`} frag={frag} />
          ))}
        </section>
      ))}
    </div>
  );
}
